import { Maatregel, useGetDossierVsmById } from "@/entities/vsm";
import { RoleGate, UserRole } from "@/features/auth";
import { Badge, Button } from "@/shared/components";
import { cn } from "@/shared/utils";
import { ComponentProps, useState } from "react";
import { VsmEditorProvider } from "../vsm-editor-provider";
import { NewEditVsmDialog } from "./newedit-vsm-dialog";
import { Versperringsmaatregel } from "./versperringsmaatregel";

interface VsmDetailsProps {
    dossierNummer: string;
}

export function VsmDetails({ dossierNummer }: VsmDetailsProps) {
    const { data: vsm, isPending, error } = useGetDossierVsmById(dossierNummer);
    const [vsmDialogOpen, setVsmDialogOpen] = useState(false);

    if (isPending) {
        return <p>VSM wordt geladen...</p>;
    }

    if (error) {
        return <p>Fout bij het laden van VSM: {error.message}</p>;
    }

    const maatregelen = Maatregel.withRijdtGroep(vsm?.maatregelen ?? []);
    const rijdtNiet = maatregelen.filter(Maatregel.filterBy("RijdtNiet"));
    const rijdt = maatregelen.filter(Maatregel.filterBy("Rijdt"));
    const aanvullend = maatregelen.filter(Maatregel.filterBy("Aanvullend"));

    return (
        <div data-variant="view" className="group text-content-emphasis flex flex-col gap-4">
            <div className="flex justify-between">
                <h2 className="pt-1 text-xl font-bold">Versperringsmaatregelen</h2>
                <RoleGate roles={[UserRole.REGIEVOERDER]}>
                    {vsm && (
                        <Button variant="secondary" size="s" onClick={() => setVsmDialogOpen(true)}>
                            VSM aanpassen
                        </Button>
                    )}
                </RoleGate>
            </div>
            {!vsm && (
                <div>
                    <p className="text-content-subtle pb-4">Er is geen versperringsmaatregel toegevoegd</p>
                    <RoleGate roles={[UserRole.REGIEVOERDER]}>
                        <Button variant="secondary" size="s" onClick={() => setVsmDialogOpen(true)}>
                            VSM toevoegen
                        </Button>
                    </RoleGate>
                </div>
            )}
            {vsm && (
                <>
                    <Badge className="mr-auto" variant={"blue-subtle"} data-testid="vsm-status">
                        {vsm.vsmStatus}
                    </Badge>
                    {vsm.toelichting && (
                        <div>
                            <h3 className="text-s font-bold">Toelichting regievoerder</h3>
                            <span className="text-content-default" data-testid="vsm-toelichting">
                                {vsm.toelichting}
                            </span>
                        </div>
                    )}
                    <VsmSection
                        type="RijdtNiet"
                        data-testid="vsm-section-rijdt-niet"
                        maatregelen={rijdtNiet}
                        isSectionEmpty={rijdtNiet.length === 0}
                    />
                    <VsmSection type="Rijdt" data-testid="vsm-section-rijdt" maatregelen={rijdt} isSectionEmpty={rijdt.length === 0} />
                    <VsmSection
                        type="Aanvullend"
                        data-testid="vsm-section-aanvullend"
                        maatregelen={aanvullend}
                        isSectionEmpty={aanvullend.length === 0}
                    />
                </>
            )}
            {vsmDialogOpen && (
                <VsmEditorProvider
                    initialMaatregelen={vsm?.maatregelen ?? []}
                    initialStatus={vsm?.vsmStatus}
                    initialToelichting={vsm?.toelichting}
                >
                    <NewEditVsmDialog dossierId={dossierNummer} open={vsmDialogOpen} onOpenChange={setVsmDialogOpen} vsm={vsm} />
                </VsmEditorProvider>
            )}
        </div>
    );
}

const typeToLabelMap: Record<"Rijdt" | "RijdtNiet" | "Aanvullend", string> = {
    Rijdt: "Blijft rijden",
    RijdtNiet: "Rijdt niet",
    Aanvullend: "Aanvullende maatregelen",
};

interface VsmSectionTitleProps {
    headerLevel: "h1" | "h2" | "h3" | "h4" | "h5" | "h6";
    type: "Rijdt" | "RijdtNiet" | "Aanvullend";
}

export function VsmSectionTitle({ type, headerLevel }: Readonly<VsmSectionTitleProps>) {
    const Tag = headerLevel;
    return <Tag className="text-m leading-m font-medium text-inherit">{typeToLabelMap[type]}</Tag>;
}

interface VsmSectionListProps {
    maatregelen: Maatregel[];
    isSectionEmpty: boolean;
}

export function VsmMaatregelList({ maatregelen, isSectionEmpty }: Readonly<VsmSectionListProps>) {
    return (
        <>
            {isSectionEmpty && <p className="text-s text-content-subtle">Geen treinseries</p>}
            {maatregelen.map((maatregel) => (
                <Versperringsmaatregel key={maatregel.id} maatregel={maatregel} />
            ))}
        </>
    );
}

type VsmSectionProps = ComponentProps<"div"> & {
    type: "Rijdt" | "RijdtNiet" | "Aanvullend";
    maatregelen: Maatregel[];
    isSectionEmpty: boolean;
};

export function VsmSection({ type, maatregelen, isSectionEmpty, className, ...props }: Readonly<VsmSectionProps>) {
    return (
        <div className={cn("flex flex-col gap-2", className)} {...props}>
            <VsmSectionTitle type={type} headerLevel="h3" />
            <VsmMaatregelList isSectionEmpty={isSectionEmpty} maatregelen={maatregelen} />
        </div>
    );
}
